/**
 * 모듈 숙련도 컨트롤러 (순수, Phaser-free — 단위 테스트 대상).
 *
 * 모듈 성능의 **세 번째 성장축** — 레벨(강화석)·등급(합성)과 직교. 모듈을 쓸수록 누적되는 XP가
 * 노드별 임계(requiredXp)에 도달하면 숙련도 노드가 **자동 해금**되어
 * 발사체 효과를 강화한다. **moduleId 기준 회차 자산**이며, 인스턴스별로 저장하지 않고 환생 시 초기화된다.
 *
 * 노드 정의(requiredXp/effect/branch)는 TSV가 SSoT라 컨트롤러가 보관하지 않는다 — 해금/효과 resolver는
 * 호출자가 `masteryNodesOf(moduleId)`를 주입한다(데이터 제어 + 컨트롤러는 XP·분기 상태만 소유).
 *
 * 분기(레벨 3 A/B)는 상호배타 — selectedBranch가 일치할 때만 적용. 미선택이면 분기 노드 전부 미적용(지시문 §5).
 */
import type { ModuleEffectData } from "../modules/ModuleEffect.js";
import type {
  ModuleMasteryBranch,
  ModuleMasteryNodeDefinition,
} from "../data/moduleMasteryDefinitions.js";
import type { ModuleMasterySaveState, ModuleMasteryState } from "../models/ModuleBrickSaveData.js";

export interface ModuleMasteryXpWeights {
  readonly activation: number;
  readonly hit: number;
  readonly kill: number;
}

const DEFAULT_XP_WEIGHTS: ModuleMasteryXpWeights = {
  activation: 1,
  hit: 2,
  kill: 5,
};

export class ModuleMasteryController {
  private readonly states = new Map<string, ModuleMasteryState>();

  /** 초기 맵 주입(저장 복원). 손상값은 ModuleBrickSaveSystem.normalizeModuleMastery가 이미 정규화했다고 가정하되 방어적 복제. */
  constructor(initial?: ModuleMasterySaveState, private readonly xpWeights: ModuleMasteryXpWeights = DEFAULT_XP_WEIGHTS) {
    if (initial) {
      for (const [moduleId, state] of Object.entries(initial)) {
        this.states.set(moduleId, {
          xp: Math.max(0, Math.floor(state.xp)),
          ...(state.selectedBranch ? { selectedBranch: state.selectedBranch } : {}),
        });
      }
    }
  }

  /** moduleId의 상태 (없으면 생성). */
  private ensure(moduleId: string): ModuleMasteryState {
    let s = this.states.get(moduleId);
    if (!s) {
      s = { xp: 0 };
      this.states.set(moduleId, s);
    }
    return s;
  }

  /** XP 누적 — amount×weight만큼 증가(음수/0 무시). */
  private addWeightedXp(moduleId: string, amount: number, weight: number): void {
    if (amount <= 0) return;
    const delta = Math.floor(amount) * Math.max(0, Math.floor(weight));
    if (delta <= 0) return;
    this.ensure(moduleId).xp += delta;
  }

  /** 발사 XP 누적 (모듈이 경로에 포함돼 발사에 기여). */
  addActivation(moduleId: string, amount = 1): void {
    this.addWeightedXp(moduleId, amount, this.xpWeights.activation);
  }

  /** 타격 XP 누적 (이 모듈 기여 발사체가 벽돌을 맞춤). */
  addHit(moduleId: string, amount = 1): void {
    this.addWeightedXp(moduleId, amount, this.xpWeights.hit);
  }

  /** 처치 XP 누적 (이 모듈 기여 발사체가 벽돌을 파괴). */
  addKill(moduleId: string, amount = 1): void {
    this.addWeightedXp(moduleId, amount, this.xpWeights.kill);
  }

  /** moduleId의 누적 XP (미존재=0). */
  xp(moduleId: string): number {
    return this.states.get(moduleId)?.xp ?? 0;
  }

  /** moduleId의 현재 선택 분기 (미선택=undefined). */
  selectedBranch(moduleId: string): ModuleMasteryBranch | undefined {
    return this.states.get(moduleId)?.selectedBranch;
  }

  /** 분기 선택 — 레벨 3 A/B 중 적용할 한쪽을 지정. */
  selectBranch(moduleId: string, branch: ModuleMasteryBranch): void {
    this.ensure(moduleId).selectedBranch = branch;
  }

  /** 환생 시 회차 숙련도 XP와 분기 선택을 전부 초기화한다. */
  clear(): void {
    this.states.clear();
  }

  /**
   * 노드 해금 판정 (순수 resolver — 노드 정의 주입). xp >= requiredXp면 해금.
   * 비분기 노드는 임계만 충족하면 해금, 분기 노드는 추가로 selectedBranch === branch 일 때만 포함(지시문 §5).
   */
  isUnlocked(moduleId: string, node: ModuleMasteryNodeDefinition): boolean {
    if (this.xp(moduleId) < node.requiredXp) return false;
    if (node.branch === undefined) return true;
    return this.selectedBranch(moduleId) === node.branch;
  }

  /** moduleId의 해금된 노드 목록 (정의 주입). */
  unlockedNodes(
    moduleId: string,
    nodes: readonly ModuleMasteryNodeDefinition[],
  ): ModuleMasteryNodeDefinition[] {
    return nodes.filter((n) => this.isUnlocked(moduleId, n));
  }

  /** moduleId의 해금 노드 효과 배열 — CombatSystem이 DefinitionModule에 주입해 발사체 spec에 적용. */
  masteryEffects(
    moduleId: string,
    nodes: readonly ModuleMasteryNodeDefinition[],
  ): ModuleEffectData[] {
    return this.unlockedNodes(moduleId, nodes).map((n) => n.effect);
  }

  /** 영속 상태 — XP 0이고 분기 미선택인 모듈은 생략(저장 최소화). */
  toState(): ModuleMasterySaveState {
    const out: ModuleMasterySaveState = {};
    for (const [moduleId, s] of this.states) {
      if (s.xp === 0 && !s.selectedBranch) continue;
      out[moduleId] = {
        xp: s.xp,
        ...(s.selectedBranch ? { selectedBranch: s.selectedBranch } : {}),
      };
    }
    return out;
  }

  static fromState(state: ModuleMasterySaveState | undefined): ModuleMasteryController {
    return new ModuleMasteryController(state);
  }
}
